/**
 * Section tabs.
 *
 * The nav collapsed from 35 items to 15 by grouping screens that do one job —
 * money, contacts, inbox, reports — under a single destination. This strip is
 * how the grouped screens stay reachable: it renders on every screen in a
 * group, so the group behaves like one tabbed screen without the pages having
 * to be rewritten into a shared shell.
 *
 * Nothing here is a redirect. Every route still exists at its old URL, so
 * bookmarks and the command palette keep working.
 */

import { NavLink } from 'react-router-dom'
import styles from './SectionTabs.module.css'

export type SectionTab = { label: string; to: string; end?: boolean }

export const MONEY_TABS: SectionTab[] = [
  { label: 'Revenue & fees', to: '/revenue' },
  { label: 'Invoices', to: '/invoices', end: true },
  { label: 'Payments', to: '/payments' },
  { label: 'Ledger', to: '/commissions' },
  { label: 'Transactions', to: '/transactions', end: true },
]

export const CONTACT_TABS: SectionTab[] = [
  { label: 'Contacts', to: '/contacts', end: true },
  { label: 'Organizations', to: '/organizations', end: true },
  { label: 'Network', to: '/relationships' },
]

export const REPORT_TABS: SectionTab[] = [
  { label: 'Reports', to: '/reports', end: true },
  { label: 'Analytics', to: '/analytics' },
  { label: 'Builder', to: '/reports/builder' },
  { label: 'Portfolio', to: '/reports/portfolio' },
  { label: 'Player package', to: '/reports/player-package' },
  { label: 'Win / loss', to: '/deals/win-loss' },
]

export const CONTRACT_TABS: SectionTab[] = [
  { label: 'Register', to: '/contracts', end: true },
  { label: 'Incentives', to: '/incentives' },
]

export const SETTINGS_TABS: SectionTab[] = [
  { label: 'Settings', to: '/settings', end: true },
  { label: 'Team', to: '/team' },
  { label: 'Tags', to: '/tags' },
  { label: 'Vendors', to: '/vendors' },
  { label: 'Goals', to: '/goals' },
  { label: 'Data health', to: '/admin/data-health' },
]

export const PIPELINE_TABS: SectionTab[] = [
  { label: 'Pipeline', to: '/deals', end: true },
  { label: 'Templates', to: '/deals/templates' },
  { label: 'Player moves', to: '/player-transactions' },
]

/**
 * Which strip belongs to a route.
 *
 * Resolved centrally and rendered once in AppLayout rather than injected into
 * eighteen page components, each of which has its own return shape. One place
 * to read, one place to change.
 */
export function tabsForPath(pathname: string): SectionTab[] | null {
  const groups: [SectionTab[], string[]][] = [
    [MONEY_TABS, ['/revenue', '/invoices', '/payments', '/commissions', '/transactions']],
    [CONTACT_TABS, ['/contacts', '/organizations', '/relationships']],
    [REPORT_TABS, ['/reports', '/analytics', '/deals/win-loss']],
    [CONTRACT_TABS, ['/contracts', '/incentives']],
    [PIPELINE_TABS, ['/deals', '/deals/templates', '/player-transactions']],
    [SETTINGS_TABS, ['/settings', '/team', '/tags', '/vendors', '/goals', '/admin/data-health']],
  ]
  for (const [tabs, roots] of groups) {
    // Exact match, or a child route (/reports/builder) — but never a record
    // page like /contacts/abc123, where a section strip would be noise.
    if (roots.some(r => pathname === r)) return tabs
    if (roots.some(r => pathname.startsWith(r + '/') && !/\/[A-Za-z0-9]{15}/.test(pathname))) return tabs
  }
  return null
}

export default function SectionTabs({ tabs }: { tabs: SectionTab[] }) {
  return (
    <nav className={styles.bar} aria-label="Section">
      {tabs.map(t => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ''}`}
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  )
}
